import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Button } from '../ui/Button';
import { Notice, StatusBadge } from '../ui/Badge';
import { Field, field } from '../create/Field';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { IconTeam } from '../icons';
import { dateOnly } from '../../lib/format';

export type Assignment = {
  id: string;
  userId: string;
  userName: string;
  startsOn: string;
  endsOn: string | null;
  /**
   * Where this stint sits relative to the SHOP's today — settled by the service, never
   * recomputed here. Two web surfaces used to each do this arithmetic in the browser
   * while the register did it in UTC, so one screen said "Current" while the other
   * showed an em-dash for the five hours between the shop's midnight and UTC's.
   */
  phase: 'past' | 'current' | 'upcoming';
};

/**
 * Who runs this van, and who ran it before (107).
 *
 * The server holds the rule — one ACTIVE vehicle per tech, no overlaps — and it holds it
 * in words: a clash comes back as "They already have 'Unit 7' for that period — end that
 * assignment first". This editor renders that sentence UNCHANGED rather than translating
 * it into "Could not save". The guard names the van standing in the way, which is the one
 * fact needed to fix it, and a generic failure message throws exactly that away.
 *
 * `today` is the SHOP's, passed in from `WithOrgClock`. An end date is a claim somebody
 * reads back later, so a New York shop assigning a van at 9pm must not date it tomorrow —
 * the same reasoning the archive service already applies on the server side.
 */
export function AssignmentEditor({
  assetId,
  assetKind,
  assetStatus,
  assignments,
  today,
}: {
  assetId: string;
  assetKind: string;
  assetStatus: string;
  assignments: Assignment[];
  today: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const members = useQuery(trpc.org.members.queryOptions());
  const [userId, setUserId] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState('');
  const [ending, setEnding] = useState<{ id: string; date: string } | null>(null);

  const done = () => {
    void qc.invalidateQueries();
  };
  const assign = useMutation(
    trpc.asset.assignmentSet.mutationOptions({
      onSuccess: () => {
        setUserId('');
        setEndsOn('');
        done();
      },
    }),
  );
  const end = useMutation(
    trpc.asset.assignmentEnd.mutationOptions({
      onSuccess: () => {
        setEnding(null);
        done();
      },
    }),
  );

  // Techs first — they are who a van goes to — but everybody is offered, because an owner
  // who drives is a shop of four people, not an edge case.
  const people = [...(members.data ?? [])].sort((a, b) =>
    a.role === b.role ? a.name.localeCompare(b.name) : a.role === 'tech' ? -1 : 1,
  );
  const retired = assetStatus === 'retired';
  // ⚠️ The roster IS the assign control, so `?? []` alone would render a failed or
  // offline `org.members` as "Choose someone…" over an empty list — a shop of six told
  // nobody works there, with no error and no retry. That is the exact shape the honesty
  // law at the top of QueryStates forbids.
  const peopleState = shownState(members, members.data);

  return (
    <div className="space-y-4">
      {assign.error ? <Notice tone="danger">{assign.error.message}</Notice> : null}
      {end.error ? <Notice tone="danger">{end.error.message}</Notice> : null}

      {assignments.length === 0 ? (
        <EmptyState
          icon={IconTeam}
          title="Nobody is assigned to this yet"
          description={
            assetKind === 'vehicle'
              ? 'Assign the van to whoever drives it and it shows up as theirs — one vehicle per person at a time.'
              : 'Assign the tool to whoever is carrying it.'
          }
        />
      ) : (
        <ul className="divide-y divide-border text-sm">
          {assignments.map((a) => (
            <li key={a.id} className="space-y-2 py-2.5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 truncate font-medium">{a.userName}</span>
                <span className="text-xs text-muted-foreground">
                  {dateOnly(a.startsOn)} → {a.endsOn ? dateOnly(a.endsOn) : 'no end date'}
                </span>
                {a.phase === 'current' ? (
                  <StatusBadge tone="success">Current</StatusBadge>
                ) : a.phase === 'upcoming' ? (
                  <StatusBadge tone="info">Upcoming</StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">Past</StatusBadge>
                )}
                {/* A past stint has nothing left to end, and a retired asset's history is
                    frozen — the server refuses both, so neither offers a button that can
                    only come back with a refusal. */}
                {!retired && a.phase !== 'past' ? (
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() =>
                      setEnding(
                        ending?.id === a.id
                          ? null
                          : {
                              id: a.id,
                              // An upcoming stint cannot end before it starts, and the
                              // server says so in words — don't hand it the ask.
                              date: a.phase === 'upcoming' ? a.startsOn : today,
                            },
                      )
                    }
                  >
                    {ending?.id === a.id ? 'Cancel' : 'End'}
                  </Button>
                ) : null}
              </div>
              {ending?.id === a.id ? (
                <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius)] bg-surface-2 p-2">
                  <Field label="Last day">
                    <input
                      type="date"
                      className={field}
                      value={ending.date}
                      min={a.startsOn}
                      onChange={(e) => setEnding({ id: a.id, date: e.target.value })}
                    />
                  </Field>
                  <Button
                    disabled={!ending.date || end.isPending}
                    onClick={() =>
                      end.mutate({ assignmentId: a.id, endsOn: ending.date, ...idem() })
                    }
                  >
                    {end.isPending ? 'Saving…' : 'End assignment'}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {retired ? (
        <p className="text-xs text-muted-foreground">
          Retired assets keep their history and take no new assignments. Put it back in
          service to assign it again.
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-4">
          {!members.data ? (
            <div className="w-full">
              <UnresolvedState
                state={unresolved(peopleState)}
                what="who works here"
                icon={IconTeam}
                error={members.error}
                onRetry={() => void members.refetch()}
                skeleton="h-10"
              />
            </div>
          ) : (
            <>
              <StaleNotice
                show={refreshFailed(members, members.data)}
                onRetry={() => void members.refetch()}
              />
              <div className="min-w-[12rem] flex-1">
                <Field label="Assign to">
                  <select
                    className={field}
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  >
                    <option value="">Choose someone…</option>
                    {people.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name} · {m.role}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="From">
                <input
                  type="date"
                  className={field}
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                />
              </Field>
              <Field label="Until (optional)">
                <input
                  type="date"
                  className={field}
                  value={endsOn}
                  min={startsOn}
                  onChange={(e) => setEndsOn(e.target.value)}
                />
              </Field>
              <Button
                disabled={!userId || !startsOn || assign.isPending}
                onClick={() =>
                  assign.mutate({
                    assetId,
                    userId,
                    startsOn,
                    ...(endsOn ? { endsOn } : {}),
                    ...idem(),
                  })
                }
              >
                {assign.isPending ? 'Saving…' : 'Assign'}
              </Button>
              {/* ⚠️ The copy has to match what the server actually does. It used to say
                  a standing assignment "ends when somebody else takes the van" — it does
                  not: `setAssignment` never ends an existing stint, and for a VEHICLE the
                  one-per-tech guard refuses the overlap outright. Promising an automatic
                  handoff and then refusing it is worse than asking for the extra click,
                  because the refusal reads as a bug. */}
              <p className="w-full text-xs text-muted-foreground">
                Leave &ldquo;until&rdquo; empty for a standing assignment — the normal
                case.{' '}
                {assetKind === 'vehicle'
                  ? 'Nobody drives two vans at once. Their other van has to END before this one starts — the same day counts as an overlap, and so do two future assignments.'
                  : 'A tool can be out with more than one person at once.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
